import { Toolbar, Item, DataGridRef } from "devextreme-react/data-grid";
import { useRef, useState } from "react";
import { openPopup } from "../../helper/DxHelper";
import { basicSettings } from "../../helper/GridCreator/gridConfig";
import GridCreator from "../../helper/GridCreator/GridCreator";
import type { GridType, DocumentTemplate } from "@/types/types";
import { DocumentTemplateDS } from "@/dataSources/DocumentTemplateDS";
import AddDocumentTemplate from "./AddDocumentTemplate";
import { useDownloadDocumentTemplate } from "@/hooks/useDownloadDocumentTemplate";
import EditDocumentTemplate from "./EditDocumentTemplate";
import { useDocumentTemplateDelete } from "@/hooks/useDocumentTemplateDelete";
import { confirm } from "devextreme/ui/dialog";
import useHeaderChangeNoUP from "@/hooks/useHeaderChangeNoUP";
import { PopupRef } from "devextreme-react/cjs/popup";
import { ColumnCellTemplateData } from "devextreme/ui/data_grid";
import { useAppSelector } from "@/hooks/useAppSelector";

export default function DocumentTemplate() {
  useHeaderChangeNoUP("Document Template");
  const datagridRef = useRef<DataGridRef<DocumentTemplate, string>>(null);
  const addPopupRef = useRef<PopupRef>(null);
  const editPopupRef = useRef<PopupRef>(null);
  const { user } = useAppSelector((state) => state.system);

  const [editedID, setEditedID] = useState<string | null>(null);

  const { mutate: download } = useDownloadDocumentTemplate();
  const { mutate: remove } = useDocumentTemplateDelete();

  const addButtonOptions = {
    icon: "plus",
    onClick: () => {
      openPopup(addPopupRef);
    },
  };

  function renderToolbar() {
    return (
      <Toolbar>
        <Item
          location="after"
          locateInMenu={"auto"}
          widget="dxButton"
          options={addButtonOptions}
          cssClass="dx-datagrid-insert-button"
        />
        <Item name="columnChooserButton" location="after" />
        <Item name="searchPanel" location="after" />
      </Toolbar>
    );
  }

  const gridSettings: GridType<DocumentTemplate, string> = {
    ...basicSettings,
    ref: datagridRef,
    id: "suGrid",
    dataSource: DocumentTemplateDS(),
    toolbar: renderToolbar,
    columns: [
      {
        key: "cmd",
        width: user?.isAdmin ? 120 : 80,
        cellRender: cmdCellRender,
        allowExporting: false,
        allowHiding: false,
        allowResizing: false,
        showInColumnChooser: false,
      },
      {
        key: "id",
        dataField: "id",
        visible: false,
        allowEditing: false,
      },
      {
        key: "name",
        dataType: "string",
        dataField: "name",
        visible: true,
        allowEditing: false,
      },
      {
        key: "isDefault",
        dataType: "boolean",
        dataField: "isDefault",
        visible: true,
        allowEditing: false,
      },
      {
        key: "fileName",
        dataType: "string",
        dataField: "fileName",
        visible: true,
        allowEditing: false,
      },
      {
        key: "comment",
        dataType: "string",
        dataField: "comment",
        visible: true,
        allowEditing: false,
      },
      {
        key: "templateType.name",
        dataType: "string",
        dataField: "templateType.name",
        visible: true,
        allowEditing: false,
      },
    ],
  };

  function cmdCellRender(e: ColumnCellTemplateData<DocumentTemplate>) {
    return (
      <div className="flex items-center gap-x-2">
        <button
          title="edit"
          onClick={() => {
            setEditedID(e.row.data.id);
            setTimeout(() => {
              openPopup(editPopupRef);
            }, 0);
          }}
        >
          <img src="/icons/edit-icon.svg" />
        </button>
        <button
          title="download"
          onClick={() => {
            const { id, fileName } = e.row.data;
            download({ id, fileName });
          }}
        >
          <img src="/icons/download-icon.svg" />
        </button>
        {e.row.data.canDelete && user?.isAdmin ? (
          <button
            title="delete"
            onClick={() => {
              const { id, name } = e.row.data;
              const res = confirm(`Do you really want to remove ${name}?`, `Remove ${name}`);
              res.then((val) => {
                if (val) remove(id);
              });
            }}
          >
            <img src="/icons/trash-icon.svg" />
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {user?.isAdmin ? (
        <>
          <AddDocumentTemplate popupRef={addPopupRef} datagridRef={datagridRef} />
          {editedID ? (
            <EditDocumentTemplate
              id={editedID}
              popupRef={editPopupRef}
              datagridRef={datagridRef}
              setEditId={setEditedID}
            />
          ) : null}
          <GridCreator {...gridSettings} />
        </>
      ) : null}
    </>
  );
}
